import {
  AlertTriangle,
  Button,
  RefreshCw,
  Wrench,
} from "@ews-admin/global-design-system";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearError,
  createAppNoticeRequest,
  fetchAppNoticesRequest,
  selectAppNoticeLoading,
  selectAppNoticeRules,
  selectAppNoticeSaving,
  updateAppNoticeRequest,
} from "../../store/reducers/appNoticeSlice";
import {
  AppNoticeDto,
  NoticeTypeEnum,
  AppNoticePayload,
} from "../../types/appNotice.definition";

// Look up the *global* maintenance rule: env=null AND country=null. Anything
// scoped to a specific env/country is ignored here on purpose; this page only
// drives the platform-wide toggle.
function findGlobalMaintenanceRule(
  rules: AppNoticeDto[]
): AppNoticeDto | undefined {
  return rules.find(
    r =>
      r.featureType === NoticeTypeEnum.MAINTENANCE &&
      r.environment === null &&
      r.country === null
  );
}

function formatDateTime(value: string | null | undefined, locale: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export const MaintenanceConfig: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const rules = useAppSelector(selectAppNoticeRules);
  const isLoading = useAppSelector(selectAppNoticeLoading);
  const isSaving = useAppSelector(selectAppNoticeSaving);

  const [confirmEnable, setConfirmEnable] = useState(false);

  const globalRule = useMemo(() => findGlobalMaintenanceRule(rules), [rules]);
  const isOn = globalRule?.enabled === true;

  useEffect(() => {
    dispatch(
      fetchAppNoticesRequest({
        featureType: NoticeTypeEnum.MAINTENANCE,
      })
    );
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleRefresh = () =>
    dispatch(
      fetchAppNoticesRequest({
        featureType: NoticeTypeEnum.MAINTENANCE,
      })
    );

  // Switching maintenance ON: confirm first, then POST (no global rule) or PUT
  // (existing global rule) with `enabled = true`.
  const performEnable = () => {
    setConfirmEnable(false);

    const onSuccess = () => {
      toast.success(t("admin.governance.maintenance.toast.enabled"));
    };
    const onError = () => {
      toast.error(t("admin.governance.maintenance.toast.saveError"));
    };

    if (globalRule) {
      const payload: AppNoticePayload = {
        featureType: NoticeTypeEnum.MAINTENANCE,
        environment: null,
        country: null,
        enabled: true,
        validFrom: globalRule.validFrom,
        validUntil: globalRule.validUntil,
        messageEn: null,
        messageFr: null,
        messageAr: null,
        bgColor: null,
      };
      dispatch(
        updateAppNoticeRequest({
          id: globalRule.id,
          data: payload,
          onSuccess,
          onError,
        })
      );
    } else {
      const payload: AppNoticePayload = {
        featureType: NoticeTypeEnum.MAINTENANCE,
        environment: null,
        country: null,
        enabled: true,
        validFrom: null,
        validUntil: null,
        messageEn: null,
        messageFr: null,
        messageAr: null,
        bgColor: null,
      };
      dispatch(
        createAppNoticeRequest({
          data: payload,
          onSuccess,
          onError,
        })
      );
    }
  };

  // Switching OFF requires an existing global rule (otherwise OFF is already
  // the implicit state). We PUT it with `enabled = false`.
  const performDisable = () => {
    if (!globalRule) {
      toast.success(t("admin.governance.maintenance.toast.disabled"));
      return;
    }
    const payload: AppNoticePayload = {
      featureType: NoticeTypeEnum.MAINTENANCE,
      environment: null,
      country: null,
      enabled: false,
      validFrom: globalRule.validFrom,
      validUntil: globalRule.validUntil,
      messageEn: null,
      messageFr: null,
      messageAr: null,
      bgColor: null,
    };
    dispatch(
      updateAppNoticeRequest({
        id: globalRule.id,
        data: payload,
        onSuccess: () => {
          toast.success(t("admin.governance.maintenance.toast.disabled"));
        },
        onError: () => {
          toast.error(t("admin.governance.maintenance.toast.saveError"));
        },
      })
    );
  };

  const handleToggleClick = () => {
    if (isOn) {
      performDisable();
    } else {
      setConfirmEnable(true);
    }
  };

  return (
    <div className="page-container">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t("admin.governance.maintenance.title")}
          </h2>
          <p className="text-neutral-600">
            {t("admin.governance.maintenance.subtitle")}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center space-x-2"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          <span>{t("common.refresh")}</span>
        </Button>
      </div>

      {/* ── Status card ──────────────────────────────────────────────────── */}
      <div className="page-section">
        {isLoading && !globalRule ? (
          <div className="py-12 text-center">
            <RefreshCw className="mx-auto w-8 h-8 animate-spin text-neutral-400" />
            <p className="mt-2 text-sm text-neutral-500">
              {t("common.loading")}
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-neutral-200 bg-white p-8 space-y-6">
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <div
                className={`w-20 h-20 rounded-full flex items-center justify-center ${
                  isOn ? "bg-danger-50" : "bg-success-50"
                }`}
              >
                <Wrench
                  className={`w-10 h-10 ${
                    isOn ? "text-danger-600" : "text-success-600"
                  }`}
                />
              </div>
              <div>
                <span
                  className={`inline-flex items-center px-4 py-2 rounded-full text-base font-semibold ${
                    isOn
                      ? "bg-danger-100 text-danger-700"
                      : "bg-success-100 text-success-700"
                  }`}
                >
                  {isOn
                    ? t("admin.governance.maintenance.statusOn")
                    : t("admin.governance.maintenance.statusOff")}
                </span>
              </div>
              <p className="text-sm text-neutral-600 max-w-md">
                {isOn
                  ? t("admin.governance.maintenance.descriptionOn")
                  : t("admin.governance.maintenance.descriptionOff")}
              </p>
            </div>

            <div className="flex justify-center">
              <Button
                onClick={handleToggleClick}
                loading={isSaving}
                disabled={isSaving}
                className={
                  isOn
                    ? "bg-success-500 hover:bg-success-600 text-white"
                    : "bg-danger-500 hover:bg-danger-600 text-white"
                }
                size="lg"
              >
                {isOn
                  ? t("admin.governance.maintenance.turnOff")
                  : t("admin.governance.maintenance.turnOn")}
              </Button>
            </div>

            {globalRule && (
              <div className="pt-6 border-t border-neutral-200 text-center">
                <p className="text-xs text-neutral-500">
                  {t("admin.governance.maintenance.audit", {
                    // MED-1454: the audit line printed the raw author id ("par
                    // l'utilisateur 5"). The gateway now carries the author's name;
                    // the id stays as the fallback when none is recorded.
                    user:
                      globalRule.createdByName ?? globalRule.createdBy ?? "—",
                    date: formatDateTime(
                      globalRule.updatedAt ?? globalRule.createdAt,
                      i18n.language
                    ),
                  })}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmEnable}
        onClose={() => setConfirmEnable(false)}
        onConfirm={performEnable}
        title={t("admin.governance.maintenance.confirmTitle")}
        message={t("admin.governance.maintenance.confirmMessage")}
        variant="danger"
        isLoading={isSaving}
        confirmLabel={t("admin.governance.maintenance.confirmButton")}
        icon={<AlertTriangle className="w-6 h-6 text-danger-500" />}
      />
    </div>
  );
};
